"use client";

import Header from "@/components/features/Portfolio/Header";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { FRAMEWORKS_SKILL, LANGUAGES_SKILL, TOOLS_SKILL } from "@/constants";
import {
  API_ENDPOINTS,
  CAROUSEL_CONFIG,
  EDUCATION_DATA,
  FORM_CONFIG,
  PAGE_CONTENT,
  SKILLS_CONFIG,
} from "@/constants/app.constants";
import { MeData } from "@/types/me.type";
import { useQuery } from "@tanstack/react-query";
import Autoplay from "embla-carousel-autoplay";
import Image from "next/image";
import { useRef } from "react";

const fetchMe = async (): Promise<MeData> => {
  const response = await fetch(API_ENDPOINTS.ME);
  if (!response.ok) {
    throw new Error("Failed to load profile");
  }
  return response.json();
};

export default function Home() {
  const {
    data: meData,
    isLoading: isMeLoading,
    isError: isMeError,
  } = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
  });

  const pluginFramework = useRef(Autoplay({ delay: CAROUSEL_CONFIG.FRAMEWORKS_DELAY, stopOnInteraction: true }));
  const pluginLanguages = useRef(Autoplay({ delay: CAROUSEL_CONFIG.LANGUAGES_DELAY, stopOnInteraction: true }));
  const pluginTools = useRef(Autoplay({ delay: CAROUSEL_CONFIG.TOOLS_DELAY, stopOnInteraction: true }));

  return (
    <>
      <Header />
      <section className="px-5 py-8 max-w-7xl mx-auto">
        {/* About Me Section */}
        <div className="mb-8 mc-panel mc-grass-top p-6 hover:translate-y-[-2px] transition-transform duration-200 animate-slideInUp">
          <h2 className="text-2xl text-primary mb-4 uppercase">{PAGE_CONTENT.HOME.ABOUT_TITLE}</h2>
          <p className="text-card-foreground leading-relaxed text-lg">{PAGE_CONTENT.HOME.ABOUT_TEXT}</p>
        </div>

        {/* Skills Section — your inventory */}
        <div className="mb-8">
          <h2 className="text-2xl text-primary mb-6 text-center uppercase animate-fadeIn">
            {PAGE_CONTENT.HOME.SKILLS_TITLE}
          </h2>

          {/* Programming Languages */}
          <div className="mc-panel p-6 animate-slideInLeft">
            <h3 className="text-lg text-primary mb-4 text-center uppercase">{PAGE_CONTENT.HOME.LANGUAGES_TITLE}</h3>
            <Carousel
              className="w-full max-w-3xl mx-auto"
              plugins={[pluginLanguages.current]}
              orientation="horizontal"
              opts={{
                align: "center",
                loop: true,
              }}
            >
              <CarouselContent className="-ml-1">
                {LANGUAGES_SKILL.map((language) => (
                  <CarouselItem key={language} className="pl-1 basis-1/3 md:basis-1/5">
                    <div className="mc-slot p-4 flex flex-col items-center justify-center gap-2 hover:bg-primary/15 transition-colors duration-200">
                      <Image
                        src={`${FORM_CONFIG.SKILL_IMAGE_PATH}/languages/${language.toLowerCase()}.svg`}
                        alt={language}
                        width={CAROUSEL_CONFIG.SKILL_IMAGE_SIZE}
                        height={CAROUSEL_CONFIG.SKILL_IMAGE_SIZE}
                        loading="lazy"
                      />
                      <span className="text-sm uppercase text-muted-foreground">{language}</span>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>

          <div className={`grid ${SKILLS_CONFIG.GRID_COLS} gap-6 mt-6`}>
            {/* Tools */}
            <div className="mc-panel p-6 animate-slideInUp">
              <h3 className="text-lg text-primary mb-4 text-center uppercase">{PAGE_CONTENT.HOME.TOOLS_TITLE}</h3>
              <Carousel
                className={`w-full ${SKILLS_CONFIG.CAROUSEL_MAX_WIDTH} mx-auto`}
                plugins={[pluginTools.current]}
                orientation="horizontal"
                opts={{
                  align: "center",
                  loop: true,
                }}
              >
                <CarouselContent className="-ml-1">
                  {TOOLS_SKILL.map((tool) => (
                    <CarouselItem key={tool.icon} className="pl-1 basis-1/3 md:basis-1/3">
                      <div className="mc-slot p-4 flex flex-col items-center justify-center gap-2 hover:bg-primary/15 transition-colors duration-200">
                        <Image
                          src={`${FORM_CONFIG.SKILL_IMAGE_PATH}/tools/${tool.icon}.svg`}
                          alt={tool.label}
                          width={CAROUSEL_CONFIG.SKILL_IMAGE_SIZE}
                          height={CAROUSEL_CONFIG.SKILL_IMAGE_SIZE}
                          loading="lazy"
                        />
                        <span className="text-sm uppercase text-muted-foreground">{tool.label}</span>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>

            {/* Frameworks */}
            <div className="mc-panel p-6 animate-slideInRight">
              <h3 className="text-lg text-primary mb-4 text-center uppercase">{PAGE_CONTENT.HOME.FRAMEWORKS_TITLE}</h3>
              <Carousel
                className={`w-full ${SKILLS_CONFIG.CAROUSEL_MAX_WIDTH} mx-auto`}
                plugins={[pluginFramework.current]}
                orientation="horizontal"
                opts={{
                  align: "center",
                  loop: true,
                }}
              >
                <CarouselContent className="-ml-1">
                  {FRAMEWORKS_SKILL.map((framework) => (
                    <CarouselItem key={framework} className="pl-1 basis-1/3 md:basis-1/3">
                      <div className="mc-slot p-4 flex flex-col items-center justify-center gap-2 hover:bg-primary/15 transition-colors duration-200">
                        <Image
                          src={`${FORM_CONFIG.SKILL_IMAGE_PATH}/frameworks/${framework.toLowerCase()}.svg`}
                          alt={framework}
                          width={CAROUSEL_CONFIG.SKILL_IMAGE_SIZE}
                          height={CAROUSEL_CONFIG.SKILL_IMAGE_SIZE}
                          loading="lazy"
                        />
                        <span className="text-sm uppercase text-muted-foreground">{framework}</span>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          </div>
        </div>

        {/* Education Section */}
        <div className="mc-panel mc-grass-top p-6 animate-slideInUp">
          <h2 className="text-2xl mb-6 text-primary uppercase">{PAGE_CONTENT.HOME.EDUCATION_TITLE}</h2>
          <div className="space-y-4">
            {EDUCATION_DATA.map((edu, index) => (
              <div key={index} className="school mc-slot p-4 hover:bg-muted/70 transition-colors duration-200">
                <h3 className="text-lg text-primary">{edu.school}</h3>
                <p className="text-muted-foreground mt-1">{edu.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="w-full bg-primary text-primary-foreground mc-grass-top flex items-center justify-center py-8 mt-8 transition-colors duration-300 animate-fadeIn">
        <p className="text-center transition-all duration-300">
          {isMeLoading
            ? "Loading profile..."
            : isMeError
              ? "Failed to load profile."
              : `${meData?.phone ?? ""} · ${meData?.email ?? ""} · `}
          <a
            href={
              meData?.linkedIn
                ? meData.linkedIn.startsWith("http")
                  ? meData.linkedIn
                  : `https://${meData.linkedIn}`
                : "https://www.linkedin.com/in/supakit-kit"
            }
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            {meData?.linkedIn ?? "www.linkedin.com/in/supakit-kit"}
          </a>{" "}
          ·{" "}
          {meData?.address
            ? `${meData.address.building}, ${meData.address.district}, ${meData.address.city} ${meData.address.postalCode}`
            : "Ideo mobi wongsawang, Bang sue, Bangkok 10800"}
        </p>
      </footer>
    </>
  );
}
